import React, {
  forwardRef, useImperativeHandle, useRef, useCallback,
} from 'react';
import Recaptcha from 'reaptcha';
import { recaptchaSiteKey } from '../../config';

/**
 * InvisibleRecaptcha - wraps reaptcha for the reCAPTCHA v2 Invisible key.
 *
 * Usage:
 *   const recaptchaRef = useRef(null);
 *   <InvisibleRecaptcha ref={recaptchaRef} />
 *   const token = await recaptchaRef.current.execute(); // call on submit, rejects on error/expiry
 *
 * Each execute() resolves with a fresh single-use token and resets the widget afterwards,
 * so a failed submit can safely be retried.
 */
const InvisibleRecaptcha = forwardRef((props, ref) => {
  const captchaRef = useRef(null);
  const pendingRef = useRef(null);

  const settle = useCallback((method, value) => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) {
      pending[method](value);
    }
  }, []);

  const resetWidget = () => {
    if (captchaRef.current) {
      captchaRef.current.reset().catch(() => {});
    }
  };

  useImperativeHandle(ref, () => ({
    execute: () => {
      // A newer attempt replaces any attempt still waiting (e.g. a closed challenge popup).
      settle('reject', new Error('reCAPTCHA was restarted'));
      return new Promise((resolve, reject) => {
        pendingRef.current = { resolve, reject };
        if (!captchaRef.current) {
          settle('reject', new Error('reCAPTCHA is not available'));
          return;
        }
        captchaRef.current.reset()
          .catch(() => {})
          .then(() => captchaRef.current.execute())
          .catch((err) => settle('reject', err));
      });
    },
    reset: resetWidget,
  }));

  return (
    <Recaptcha
      ref={captchaRef}
      sitekey={recaptchaSiteKey}
      size="invisible"
      onVerify={(token) => {
        settle('resolve', token);
        resetWidget();
      }}
      onExpire={() => settle('reject', new Error('reCAPTCHA expired'))}
      onError={() => settle('reject', new Error('reCAPTCHA failed to load or verify'))}
    />
  );
});

InvisibleRecaptcha.displayName = 'InvisibleRecaptcha';

export default InvisibleRecaptcha;
